/**
 * Copyright (c) 2025 Lauft App Inc. All rights reserved.
 * 
 * This source code is proprietary and confidential.
 * Unauthorized copying of this file, via any medium is strictly prohibited.
 */

import React, { useState, useEffect } from 'react';
import AuthService from '../../services/authService';
import propertyService from '../../services/propertyService';

const CuratedMatchesManager = () => {
  const [users, setUsers] = useState([]);
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [properties, setProperties] = useState([]);
  const [filteredProperties, setFilteredProperties] = useState([]);
  const [curatedMatches, setCuratedMatches] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [propertySearchTerm, setPropertySearchTerm] = useState('');
  const [selectedUser, setSelectedUser] = useState(null);
  const [selectedProperties, setSelectedProperties] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [view, setView] = useState('list'); // 'list' or 'create'

  const API_URL = 'https://back-end-x7tx.onrender.com/api';

  useEffect(() => {
    fetchUsers();
    fetchProperties();
    fetchCuratedMatches();
  }, []);

  useEffect(() => {
    if (!searchTerm.trim()) {
      setFilteredUsers(users);
    } else {
      const filtered = users.filter(user => 
        user.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        user.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (user.phone && user.phone.includes(searchTerm))
      );
      setFilteredUsers(filtered);
    }
  }, [searchTerm, users]);

  useEffect(() => {
    if (!propertySearchTerm.trim()) {
      setFilteredProperties(properties);
    } else {
      const filtered = properties.filter(property => 
        property.address.toLowerCase().includes(propertySearchTerm.toLowerCase()) ||
        property.neighborhood.toLowerCase().includes(propertySearchTerm.toLowerCase())
      );
      setFilteredProperties(filtered);
    }
  }, [propertySearchTerm, properties]);

  const fetchUsers = async () => {
    setIsLoading(true);
    try {
      const response = await fetch(`${API_URL}/users/with-assignments`, {
        headers: {
          'Authorization': `Bearer ${AuthService.getToken()}`
        }
      });
      const data = await response.json();
      
      if (data.success) {
        const regularUsers = data.data.filter(user => user.role === 'user');
        setUsers(regularUsers);
        setFilteredUsers(regularUsers);
      }
    } catch (err) {
      setError('Error fetching users: ' + err.message);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchProperties = async () => {
    try {
      console.log('🏠 Fetching properties using propertyService...');
      
      // Use propertyService like ManagePropertiesTab does
      const response = await propertyService.getProperties({ limit: 10000, page: 1 });
      
      console.log('🏠 Properties response:', response);
      
      if (response && response.data) {
        console.log(`🏠 Found ${response.data.length} properties`);
        const propertyList = response.data;
        
        // Filter for "For Sale" status properties only
        const availableProperties = propertyList.filter(p => p.propertyStatus === 'For Sale');
        console.log('🏠 For Sale properties:', availableProperties.length);
        console.log('🏠 All property statuses:', [...new Set(propertyList.map(p => p.propertyStatus))]);
        
        setProperties(availableProperties);
        setFilteredProperties(availableProperties);
      } else if (response && Array.isArray(response)) {
        // Handle case where API returns array directly
        console.log(`🏠 Found ${response.length} properties (array format)`);
        const availableProperties = response.filter(p => p.propertyStatus === 'For Sale');
        console.log('🏠 For Sale properties:', availableProperties.length);
        
        setProperties(availableProperties);
        setFilteredProperties(availableProperties);
      } else {
        console.log('🏠 No properties in response or invalid format');
        setProperties([]);
        setFilteredProperties([]);
      }
    } catch (err) {
      console.error('🏠 Error fetching properties:', err);
    }
  };

  const fetchCuratedMatches = async () => {
    try {
      const response = await fetch(`${API_URL}/curated-matches`, {
        headers: {
          'Authorization': `Bearer ${AuthService.getToken()}`
        }
      });
      const data = await response.json();
      
      if (data.success) {
        setCuratedMatches(data.data);
      }
    } catch (err) {
      console.error('Error fetching curated matches:', err);
    }
  };

  const handleSelectUser = (user) => {
    setSelectedUser(user);
    setSelectedProperties([]);
    setView('create');
    setError('');
    setSuccessMessage('');
  };

  const handleToggleProperty = (property) => {
    if (selectedProperties.find(p => p._id === property._id)) {
      setSelectedProperties(selectedProperties.filter(p => p._id !== property._id));
    } else {
      if (selectedProperties.length >= 6) {
        setError('Maximum 6 properties allowed per curated match');
        return;
      }
      setSelectedProperties([...selectedProperties, property]);
    }
    setError('');
  };

  const handleSendCuratedMatch = async () => {
    if (!selectedUser || selectedProperties.length === 0) {
      setError('Please select a user and at least one property');
      return;
    }

    setIsSending(true);
    setError('');
    setSuccessMessage('');

    try {
      const response = await fetch(`${API_URL}/curated-matches`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${AuthService.getToken()}`
        },
        body: JSON.stringify({
          userId: selectedUser._id,
          propertyIds: selectedProperties.map(p => p._id)
        })
      });

      const data = await response.json();
      
      if (data.success) {
        setSuccessMessage(`✅ Successfully sent ${selectedProperties.length} curated ${selectedProperties.length === 1 ? 'match' : 'matches'} to ${selectedUser.name}!`);
        setSelectedUser(null);
        setSelectedProperties([]);
        setView('list');
        fetchCuratedMatches();
      } else {
        setError(data.error || 'Failed to send curated matches');
      }
    } catch (err) {
      setError('Error sending curated matches: ' + err.message);
    } finally {
      setIsSending(false);
    }
  };

  const handleDeleteMatch = async (matchId) => {
    if (!window.confirm('Are you sure you want to delete this curated match?')) {
      return;
    }

    try {
      const response = await fetch(`${API_URL}/curated-matches/${matchId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${AuthService.getToken()}`
        }
      });

      const data = await response.json();
      
      if (data.success) {
        setSuccessMessage('Curated match deleted successfully');
        fetchCuratedMatches();
      } else {
        setError('Failed to delete curated match');
      }
    } catch (err) {
      setError('Error deleting curated match: ' + err.message);
    }
  };

  const formatDate = (date) => {
    return new Date(date).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit'
    });
  };

  const getStatusBadge = (match) => {
    const now = new Date();
    const expiresAt = new Date(match.expiresAt);
    
    if (match.status === 'completed') {
      return <span className="badge badge-success">Completed</span>;
    } else if (match.status === 'expired' || now > expiresAt) {
      return <span className="badge badge-danger">Expired</span>;
    } else {
      const daysLeft = Math.ceil((expiresAt - now) / (1000 * 60 * 60 * 24));
      return <span className="badge badge-primary">{daysLeft}d left</span>;
    }
  };

  // List View
  if (view === 'list') {
    return (
      <div className="content-container">
        <div className="section-header">
          <h2 className="section-title">Curated Matches Manager</h2>
          <button 
            onClick={() => setView('create')} 
            className="btn btn-primary"
          >
            + Create New Match
          </button>
        </div>

        {error && (
          <div className="alert alert-danger mb-4">
            {error}
          </div>
        )}

        {successMessage && (
          <div className="alert alert-success mb-4">
            {successMessage}
          </div>
        )}

        {/* Recent Matches */}
        <div className="form-section">
          <div className="form-section-header">
            <h3>Recent Curated Matches</h3>
            <span className="total-count">{curatedMatches.length} total</span>
          </div>
          
          <div className="form-section-body">
            {isLoading ? (
              <div className="loading-spinner">Loading...</div>
            ) : curatedMatches.length === 0 ? (
              <div className="empty-state">
                <p>No curated matches yet. Create one to get started!</p>
              </div>
            ) : (
              <div className="table-container">
                <table className="data-table">
                  <thead>
                    <tr>
                      <th>User</th>
                      <th>Properties</th>
                      <th>Status</th>
                      <th>Progress</th>
                      <th>Created</th>
                      <th>Expires</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {curatedMatches.map(match => (
                      <tr key={match._id}>
                        <td>
                          <div className="user-info">
                            <div className="user-name">{match.user.name}</div>
                            <div className="user-email">{match.user.email}</div>
                          </div>
                        </td>
                        <td>
                          <strong>{match.properties.length}</strong> properties
                        </td>
                        <td>
                          {getStatusBadge(match)}
                        </td>
                        <td>
                          <div className="progress-text">
                            {match.viewedProperties.length} / {match.properties.length} viewed
                          </div>
                        </td>
                        <td>
                          <div className="date-text">
                            {formatDate(match.createdAt)}
                          </div>
                          <div className="creator-text">
                            by {match.createdBy.role === 'admin' ? 'Admin' : match.createdBy.name}
                          </div>
                        </td>
                        <td>
                          <div className="date-text">
                            {formatDate(match.expiresAt)}
                          </div>
                        </td>
                        <td>
                          <button
                            onClick={() => handleDeleteMatch(match._id)}
                            className="btn btn-danger btn-sm"
                          >
                            Delete
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>

        <style jsx>{`
          .section-header {
            display: flex;
            justify-content: space-between;
            align-items: center;
            margin-bottom: 20px;
          }
          
          .section-title {
            font-size: 1.5rem;
            font-weight: 600;
            color: #333;
            margin: 0;
          }
          
          .form-section-header {
            display: flex;
            justify-content: space-between;
            align-items: center;
            padding-bottom: 15px;
            border-bottom: 2px solid #e9ecef;
          }
          
          .form-section-header h3 {
            margin: 0;
            font-size: 1.125rem;
            color: #333;
          }
          
          .total-count {
            color: #666;
            font-weight: 500;
          }
          
          .empty-state {
            text-align: center;
            padding: 60px 20px;
            color: #999;
          }
          
          .user-info {
            display: flex;
            flex-direction: column;
            gap: 4px;
          }
          
          .user-name {
            font-weight: 600;
            color: #333;
          }
          
          .user-email {
            font-size: 0.875rem;
            color: #666;
          }
          
          .progress-text {
            font-weight: 500;
            color: #333;
          }
          
          .date-text {
            color: #333;
            font-size: 0.875rem;
          }
          
          .creator-text {
            color: #666;
            font-size: 0.875rem;
          }
          
          .badge {
            padding: 4px 8px;
            border-radius: 4px;
            font-size: 0.75rem;
            font-weight: 600;
            text-transform: uppercase;
          }
          
          .badge-success {
            background: #d4edda;
            color: #155724;
          }
          
          .badge-danger {
            background: #f8d7da;
            color: #721c24;
          }
          
          .badge-primary {
            background: #d1ecf1;
            color: #0c5460;
          }
          
          .btn-sm {
            padding: 6px 12px;
            font-size: 0.875rem;
          }
        `}</style>
      </div>
    );
  }

  // Create View
  return (
    <div className="content-container">
      <div className="section-header">
        <h2 className="section-title">Create Curated Matches</h2>
        <button 
          onClick={() => {
            setView('list');
            setSelectedUser(null);
            setSelectedProperties([]);
            setError('');
            setSuccessMessage('');
          }} 
          className="btn btn-secondary"
        >
          ← Back to List
        </button>
      </div>

      {error && (
        <div className="alert alert-danger mb-4">
          {error}
        </div>
      )}

      {successMessage && (
        <div className="alert alert-success mb-4">
          {successMessage}
        </div>
      )}

      {/* Step 1: Select User */}
      <div className="form-section mb-4">
        <div className="form-section-header">
          <h3>Step 1: Select User</h3>
          {selectedUser && (
            <button 
              onClick={() => setSelectedUser(null)}
              className="btn btn-secondary btn-sm"
            >
              Change User
            </button>
          )}
        </div>
        
        <div className="form-section-body">
          {selectedUser ? (
            <div className="selected-user-card">
              <div className="user-info">
                <div className="user-name">{selectedUser.name}</div>
                <div className="user-email">{selectedUser.email}</div>
                <div className="user-phone">{selectedUser.phone || 'No phone'}</div>
              </div>
            </div>
          ) : (
            <>
              <div className="search-box mb-3">
                <input
                  type="text"
                  placeholder="Search users by name, email, or phone..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="form-control"
                />
              </div>
              
              <div className="user-list">
                {filteredUsers.map(user => (
                  <div 
                    key={user._id} 
                    className="user-card"
                    onClick={() => handleSelectUser(user)}
                  >
                    <div className="user-info">
                      <div className="user-name">{user.name}</div>
                      <div className="user-email">{user.email}</div>
                    </div>
                    <button className="btn btn-primary btn-sm">Select</button>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
      </div>

      {/* Step 2: Select Properties */}
      {selectedUser && (
        <div className="form-section mb-4">
          <div className="form-section-header">
            <h3>Step 2: Select Properties ({selectedProperties.length}/6)</h3>
          </div>
          
          <div className="form-section-body">
            <div className="search-box mb-3">
              <input
                type="text"
                placeholder="Search properties by address or neighborhood..."
                value={propertySearchTerm}
                onChange={(e) => setPropertySearchTerm(e.target.value)}
                className="form-control"
              />
            </div>

            {selectedProperties.length > 0 && (
              <div className="selected-properties mb-3">
                <h4>Selected Properties:</h4>
                <div className="property-grid">
                  {selectedProperties.map(property => (
                    <div key={property._id} className="property-card selected">
                      <img 
                        src={property.imageUrl} 
                        alt={property.address}
                        className="property-image"
                      />
                      <div className="property-info">
                        <div className="property-address">{property.address}</div>
                        <div className="property-neighborhood">{property.neighborhood}</div>
                        <div className="property-price">${property.price.toLocaleString()}</div>
                      </div>
                      <button
                        onClick={() => handleToggleProperty(property)}
                        className="btn btn-danger btn-sm"
                      >
                        Remove
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <h4>Available Properties:</h4>
            <div className="property-grid">
              {filteredProperties
                .filter(p => !selectedProperties.find(sp => sp._id === p._id))
                .slice(0, 50)
                .map(property => (
                  <div key={property._id} className="property-card">
                    <img 
                      src={property.imageUrl} 
                      alt={property.address}
                      className="property-image"
                    />
                    <div className="property-info">
                      <div className="property-address">{property.address}</div>
                      <div className="property-neighborhood">{property.neighborhood}</div>
                      <div className="property-price">${property.price.toLocaleString()}</div>
                    </div>
                    <button
                      onClick={() => handleToggleProperty(property)}
                      className="btn btn-primary btn-sm"
                      disabled={selectedProperties.length >= 6}
                    >
                      Add
                    </button>
                  </div>
                ))}
            </div>
          </div>
        </div>
      )}

      {/* Step 3: Send */}
      {selectedUser && selectedProperties.length > 0 && (
        <div className="form-section">
          <div className="form-section-body">
            <div className="send-section">
              <h3>Ready to Send!</h3>
              <p>
                You're about to send <strong>{selectedProperties.length}</strong> curated {selectedProperties.length === 1 ? 'match' : 'matches'} to <strong>{selectedUser.name}</strong>.
              </p>
              <p className="text-muted">
                They will receive a push notification and have 3 days to review these properties.
              </p>
              <button
                onClick={handleSendCuratedMatch}
                disabled={isSending}
                className="btn btn-primary btn-lg"
              >
                {isSending ? 'Sending...' : `Send ${selectedProperties.length} Curated ${selectedProperties.length === 1 ? 'Match' : 'Matches'}`}
              </button>
            </div>
          </div>
        </div>
      )}

      <style jsx>{`
        .section-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 20px;
        }
        
        .section-title {
          font-size: 1.5rem;
          font-weight: 600;
          color: #333;
          margin: 0;
        }
        
        .form-section-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding-bottom: 15px;
          border-bottom: 2px solid #e9ecef;
        }
        
        .form-section-header h3 {
          margin: 0;
          font-size: 1.125rem;
          color: #333;
        }
        
        .search-box {
          width: 100%;
        }
        
        .search-box input {
          font-size: 16px;
          padding: 12px;
        }
        
        .user-list {
          display: flex;
          flex-direction: column;
          gap: 10px;
          max-height: 400px;
          overflow-y: auto;
        }
        
        .user-card {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 15px;
          border: 1px solid #e9ecef;
          border-radius: 8px;
          cursor: pointer;
          transition: all 0.2s;
        }
        
        .user-card:hover {
          border-color: #FFD700;
          background: #fffef8;
        }
        
        .selected-user-card {
          padding: 20px;
          border: 2px solid #FFD700;
          border-radius: 8px;
          background: #fffef8;
        }
        
        .user-info {
          display: flex;
          flex-direction: column;
          gap: 4px;
        }
        
        .user-name {
          font-weight: 600;
          color: #333;
          font-size: 1rem;
        }
        
        .user-email {
          color: #666;
          font-size: 0.875rem;
        }
        
        .user-phone {
          color: #999;
          font-size: 0.875rem;
        }
        
        .property-grid {
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
          gap: 15px;
          margin-top: 15px;
        }
        
        .property-card {
          border: 1px solid #e9ecef;
          border-radius: 8px;
          overflow: hidden;
          transition: all 0.2s;
        }
        
        .property-card:hover {
          border-color: #FFD700;
          box-shadow: 0 2px 8px rgba(0,0,0,0.1);
        }
        
        .property-card.selected {
          border: 2px solid #FFD700;
          background: #fffef8;
        }
        
        .property-image {
          width: 100%;
          height: 180px;
          object-fit: cover;
        }
        
        .property-info {
          padding: 12px;
        }
        
        .property-address {
          font-weight: 600;
          color: #333;
          font-size: 0.875rem;
          margin-bottom: 4px;
        }
        
        .property-neighborhood {
          color: #666;
          font-size: 0.75rem;
          margin-bottom: 8px;
        }
        
        .property-price {
          font-weight: 700;
          color: #FFD700;
          font-size: 1rem;
        }
        
        .property-card button {
          width: 100%;
          margin-top: 8px;
        }
        
        .selected-properties {
          padding: 15px;
          background: #f8f9fa;
          border-radius: 8px;
        }
        
        .selected-properties h4 {
          margin: 0 0 15px 0;
          font-size: 1rem;
          color: #333;
        }
        
        .send-section {
          text-align: center;
          padding: 30px;
        }
        
        .send-section h3 {
          color: #333;
          margin-bottom: 15px;
        }
        
        .send-section p {
          color: #666;
          margin-bottom: 10px;
        }
        
        .text-muted {
          color: #999;
          font-size: 0.875rem;
        }
        
        .btn-lg {
          padding: 15px 30px;
          font-size: 1.125rem;
          margin-top: 20px;
        }
        
        .btn-sm {
          padding: 6px 12px;
          font-size: 0.875rem;
        }
        
        .mb-3 {
          margin-bottom: 15px;
        }
        
        .mb-4 {
          margin-bottom: 20px;
        }
      `}</style>
    </div>
  );
};

export default CuratedMatchesManager;